import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * Age Verification Component
 * Handles age verification popup with multiple verification methods
 * 
 * @extends {Component}
 */
class AgeVerificationComponent extends Component {
  requiredRefs = ['dialog', 'form', 'errorMessage'];

  connectedCallback() {
    super.connectedCallback();
    
    // Initialize event listeners
    this.initializeListeners();
    
    // Check if user is already verified
    if (this.isVerified()) {
      return;
    }

    // Show the verification modal
    this.showVerification();
  }

  /**
   * Checks if user has already been verified
   * @returns {boolean}
   */
  isVerified() {
    const storageKey = this.getStorageKey();
    const verified = localStorage.getItem(storageKey);
    
    if (!verified) return false;
    
    // Check if verification has expired (optional - can be configured)
    const verificationData = JSON.parse(verified);
    const expirationDays = this.getExpirationDays();
    
    if (expirationDays > 0) {
      const expirationDate = new Date(verificationData.timestamp);
      expirationDate.setDate(expirationDate.getDate() + expirationDays);
      
      if (new Date() > expirationDate) {
        localStorage.removeItem(storageKey);
        return false;
      }
    }
    
    return true;
  }

  /**
   * Gets the storage key for verification
   * @returns {string}
   */
  getStorageKey() {
    return `age_verified_${this.getRequiredAge()}`;
  }

  /**
   * Gets the required age from settings
   * @returns {number}
   */
  getRequiredAge() {
    return parseInt(this.dataset.requiredAge || '18', 10);
  }

  /**
   * Gets the verification method from settings
   * @returns {string}
   */
  getVerificationMethod() {
    return this.dataset.verificationMethod || 'ask';
  }

  /**
   * Gets expiration days from settings
   * @returns {number}
   */
  getExpirationDays() {
    return parseInt(this.dataset.expirationDays || '365', 10);
  }

  /**
   * Shows the verification modal
   */
  showVerification() {
    const { dialog } = this.refs;
    const method = this.getVerificationMethod();
    
    // Show appropriate form based on method
    this.showFormForMethod(method);
    
    // Show the dialog
    if (dialog instanceof HTMLDialogElement) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        dialog.showModal();
        document.body.style.overflow = 'hidden';
      });
    }
  }

  /**
   * Shows the appropriate form based on verification method
   * @param {string} method
   */
  showFormForMethod(method) {
    const askForm = this.querySelector('[data-age-form="ask"]');
    const enterForm = this.querySelector('[data-age-form="enter"]');
    const checkboxForm = this.querySelector('[data-age-form="checkbox"]');
    
    // Hide all forms
    [askForm, enterForm, checkboxForm].forEach(form => {
      if (form) form.style.display = 'none';
    });
    
    // Show the appropriate form
    switch(method) {
      case 'ask':
        if (askForm) askForm.style.display = 'block';
        break;
      case 'enter':
        if (enterForm) enterForm.style.display = 'block';
        break;
      case 'checkbox':
        if (checkboxForm) checkboxForm.style.display = 'block';
        break;
    }
  }

  /**
   * Handles form submission
   * @param {Event} event
   */
  handleSubmit = (event) => {
    event.preventDefault();
    
    const method = this.getVerificationMethod();
    const requiredAge = this.getRequiredAge();
    let isValid = false;
    
    switch(method) {
      case 'ask':
        isValid = this.handleAskMethod();
        break;
      case 'enter':
        isValid = this.handleEnterMethod(requiredAge);
        break;
      case 'checkbox':
        isValid = this.handleCheckboxMethod();
        break;
    }
    
    if (isValid) {
      this.verifyUser();
      this.closeVerification();
    } else {
      this.showError();
    }
  };

  /**
   * Handles "ask" method (Yes/No buttons)
   * @returns {boolean}
   */
  handleAskMethod() {
    const yesButton = this.querySelector('[data-age-action="yes"]');
    const noButton = this.querySelector('[data-age-action="no"]');
    
    // This is handled by button clicks, not form submission
    return false;
  }

  /**
   * Handles "enter" method (date input)
   * @param {number} requiredAge
   * @returns {boolean}
   */
  handleEnterMethod(requiredAge) {
    const yearInput = this.querySelector('[data-age-input="year"]');
    const monthInput = this.querySelector('[data-age-input="month"]');
    const dayInput = this.querySelector('[data-age-input="day"]');
    
    if (!yearInput || !monthInput || !dayInput) return false;
    
    const year = parseInt(yearInput.value, 10);
    const month = parseInt(monthInput.value, 10) - 1; // Month is 0-indexed
    const day = parseInt(dayInput.value, 10);
    
    if (!year || !month || !day) {
      return false;
    }
    
    const birthDate = new Date(year, month, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= requiredAge;
  }

  /**
   * Handles "checkbox" method (checkbox confirmation)
   * @returns {boolean}
   */
  handleCheckboxMethod() {
    const checkbox = this.querySelector('[data-age-checkbox]');
    return checkbox && checkbox.checked;
  }

  /**
   * Handles Yes button click
   */
  handleYes = () => {
    this.verifyUser();
    this.closeVerification();
  };

  /**
   * Handles No button click
   */
  handleNo = () => {
    this.showError();
    // Optionally redirect to a "not allowed" page
    const redirectUrl = this.dataset.redirectUrl;
    if (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 2000);
    }
  };

  /**
   * Verifies the user and stores in localStorage
   */
  verifyUser() {
    const storageKey = this.getStorageKey();
    const verificationData = {
      verified: true,
      timestamp: new Date().toISOString(),
      age: this.getRequiredAge()
    };
    
    localStorage.setItem(storageKey, JSON.stringify(verificationData));
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('age:verified', {
      detail: { age: this.getRequiredAge() }
    }));
  }

  /**
   * Closes the verification modal
   */
  closeVerification() {
    const { dialog } = this.refs;
    
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.close();
      document.body.style.overflow = '';
      
      // Unblock page content
      const mainContent = document.querySelector('#MainContent');
      const headerGroup = document.querySelector('#header-group');
      
      if (mainContent) {
        mainContent.style.opacity = '';
        mainContent.style.pointerEvents = '';
      }
      
      if (headerGroup) {
        headerGroup.style.opacity = '';
        headerGroup.style.pointerEvents = '';
      }
    }
  }

  /**
   * Shows error message
   */
  showError() {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.style.display = 'block';
      errorMessage.textContent = this.dataset.errorMessage || 'You must be of legal age to access this site.';
    }
  }

  /**
   * Hides error message
   */
  hideError() {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }
  }

  /**
   * Initializes event listeners
   */
  initializeListeners() {
    const { form } = this.refs;
    
    if (form) {
      form.addEventListener('submit', this.handleSubmit);
    }
    
    // Yes/No buttons
    const yesButton = this.querySelector('[data-age-action="yes"]');
    const noButton = this.querySelector('[data-age-action="no"]');
    
    if (yesButton) {
      yesButton.addEventListener('click', this.handleYes);
    }
    
    if (noButton) {
      noButton.addEventListener('click', this.handleNo);
    }
    
    // Hide error when user interacts with form
    const inputs = this.querySelectorAll('input');
    inputs.forEach(input => {
      input.addEventListener('input', () => this.hideError());
    });
  }
}

if (!customElements.get('age-verification')) {
  customElements.define('age-verification', AgeVerificationComponent);
}

/**
 * Check age verification on page load and block content if not verified
 */
function checkAgeVerification() {
  const ageVerification = document.querySelector('age-verification');
  if (!ageVerification) return;
  
  const requiredAge = parseInt(ageVerification.dataset.requiredAge || '18', 10);
  const storageKey = `age_verified_${requiredAge}`;
  const verified = localStorage.getItem(storageKey);
  
  if (!verified) {
    // Block page content until verified
    const mainContent = document.querySelector('#MainContent');
    const headerGroup = document.querySelector('#header-group');
    
    if (mainContent) {
      mainContent.style.opacity = '0.3';
      mainContent.style.pointerEvents = 'none';
    }
    
    if (headerGroup) {
      headerGroup.style.opacity = '0.3';
      headerGroup.style.pointerEvents = 'none';
    }
  } else {
    // Unblock content if already verified
    const mainContent = document.querySelector('#MainContent');
    const headerGroup = document.querySelector('#header-group');
    
    if (mainContent) {
      mainContent.style.opacity = '';
      mainContent.style.pointerEvents = '';
    }
    
    if (headerGroup) {
      headerGroup.style.opacity = '';
      headerGroup.style.pointerEvents = '';
    }
  }
}

/**
 * Unblock page content after verification
 */
function unblockContent() {
  const mainContent = document.querySelector('#MainContent');
  const headerGroup = document.querySelector('#header-group');
  
  if (mainContent) {
    mainContent.style.opacity = '';
    mainContent.style.pointerEvents = '';
  }
  
  if (headerGroup) {
    headerGroup.style.opacity = '';
    headerGroup.style.pointerEvents = '';
  }
}

// Listen for verification event
document.addEventListener('age:verified', unblockContent);

// Run check on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkAgeVerification);
} else {
  checkAgeVerification();
}
